import { useEffect, useMemo, useRef, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import "./style.scss";

type InfoCardProps = {
  title?: string | JSX.Element;
  tooltip: string;
  children?: JSX.Element;
  parallaxImg?: string;
  disableParallax?: boolean;
  key?: React.Key;
};
const GlassCard: React.FC<InfoCardProps> = ({
  title,
  tooltip,
  children,
  parallaxImg,
  disableParallax,
  key
}) => {
  const [offset, setOffset] = useState([0, 0]);
  const [rawOffset, setRawOffset] = useState([0, 0]);
  const [dimensions, setDimensions] = useState([0, 0]);
  const [hover, setHover] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setDimensions([
      ref.current?.clientWidth || 0,
      ref.current?.clientHeight || 0
    ]);
  }, [ref]);

  const img = useMemo(
    () =>
      parallaxImg ? (
        <img
          className='extra-card-img'
          style={{
            pointerEvents: "none",
            objectPosition: hover
              ? `${50 + offset[0]}% ${50 - offset[1] * 2}%`
              : "50% 50%"
          }}
          alt={tooltip}
          src={parallaxImg}></img>
      ) : (
        <></>
      ),
    [offset, parallaxImg, tooltip, hover]
  );

  return (
    <OverlayTrigger
      key={key}
      placement={"bottom"}
      overlay={<Tooltip id={"tooltip-" + tooltip}>{tooltip}</Tooltip>}>
      <div
        ref={ref}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onMouseMove={e => {
          if (disableParallax) {
            setRawOffset([0, 0]);
            setOffset([0, 0]);
          } else {
            setRawOffset([e.nativeEvent.offsetX, e.nativeEvent.offsetY]);
            setOffset([
              -(dimensions[0] / 2 - e.nativeEvent.offsetX) / 15,
              (dimensions[1] / 2 - e.nativeEvent.offsetY) / 15
            ]);
          }
        }}>
        <div
          className='extra-card'
          style={{
            position: "relative",
            zIndex: hover ? 10 : 1,
            boxShadow: hover
              ? `${-offset[0] / 4}px ${offset[1] / 4}px 1px rgba(0,0,0,0.4), ${
                  -offset[0] / 14
                }px ${offset[1] / 14}px 2px rgba(0,0,0,0.8)`
              : "0px 0px 0px #000000",
            transform: hover
              ? `scale(1.2) perspective(20cm) rotateX(${offset[1]}deg) rotateY(${offset[0]}deg)`
              : "scale(1)"
          }}
          key={key}>
          <p className='extra-card-title'>{title}</p>

          {children ? children : img}

          <div
            style={{
              width: `${50 + (offset[1] - offset[0])}%`,
              opacity: hover && !disableParallax ? 1 : 0,
              transform: `translateX(${-rawOffset[0]}px) translateY(${-rawOffset[1]}px)`,
              filter: `blur(${3 + (offset[1] - offset[0]) / 20}rem)`,
              ...specularEffectStyle
            }}
          />
        </div>
      </div>
    </OverlayTrigger>
  );
};

export default GlassCard;

const specularEffectStyle: React.CSSProperties = {
  pointerEvents: "none",
  position: "absolute",
  backgroundColor: "rgba(255,255,255,0.9)",
  aspectRatio: "1",
  zIndex: 20,
  borderRadius: "100%",
  top: "25%",
  left: "25%",
  transition: "cubic-bezier(0.075, 0.82, 0.165, 1) 1s"
};
